import React, { useEffect } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Container } from '../../components/common/Container'
import { Post } from '../../components/feed/Post'
import { ButtonVariant } from '../../components/common/Button'
import { LinkButton } from '../../components/common/LinkButton'
import { reverseUrl } from '../../navigation/reverseUrl'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { IconChoices } from '../../components/common/Icon'
import { SpinnerWrapper } from '../../components/common/SpinnerWrapper'
import { ErrorView } from '../error/ErrorView'
import { IllustrationChoices } from '../../components/common/Illustration'
import { isPostAccessError, postRowQueries } from '../../api/posts'
import { removePostFromLists } from '../../components/feed/postRowCache'

export const PostView = (): React.ReactNode => {
  const { id } = useParams()
  const { translate } = useAppTranslation()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const {
    data: row,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(postRowQueries.row(id))

  const accessDenied = isPostAccessError(error)
  const post = accessDenied ? undefined : row

  useEffect(() => {
    if (id && accessDenied) {
      removePostFromLists(queryClient, id)
    }
  }, [id, accessDenied, queryClient])

  if (!id) {
    return <Navigate to="/not-found" replace />
  }

  if (isLoading) {
    return <SpinnerWrapper />
  }

  return (
    <div className="flex flex-1 flex-col items-center overflow-y-scroll bg-gray-100">
      <Container className="md:max-w-4xl md:pb-0" overflowScrollY={false}>
        <LinkButton
          className="hover:bg-neutral-92"
          variant={ButtonVariant.TEXT}
          label={translate('feedView.title')}
          icon={IconChoices.ARROW_LEFT}
          size="sm"
          url={reverseUrl('feed')}
          urlIsExternal={false}
        />

        {post && (
          <div className="pb-1 md:pb-6">
            <Post
              key={post.id}
              row={post}
              displayComments
              targetCommentId={searchParams.get('commentId') || undefined}
            />
          </div>
        )}
        {!post && (accessDenied || !isError) && (
          <ErrorView
            illustration={IllustrationChoices.EMPTY}
            title={translate('postView.errorView.removed.title')}
            subtitle={translate('postView.errorView.removed.subtitle')}
            titleWrapperClassName="mt-4"
          />
        )}
        {!post && isError && !accessDenied && (
          <ErrorView
            illustration={IllustrationChoices.EMPTY}
            title={translate('flashMessage.somethingWentWrongError')}
            titleWrapperClassName="mt-4"
            buttons={[
              {
                id: 'retry-post',
                button: {
                  label: translate('common.actions.tryAgain'),
                  variant: ButtonVariant.PRIMARY,
                  onClick: () => refetch(),
                },
              },
            ]}
          />
        )}
      </Container>
    </div>
  )
}
